import { useNostr } from '@nostrify/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import type { NostrEvent } from '@nostrify/nostrify';

/**
 * Fetches kind-1 notes from the accounts the current user follows,
 * plus the user's own notes, sorted newest-first.
 */
export function useNoteFeed(limit = 50) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  return useQuery<NostrEvent[]>({
    queryKey: ['note-feed', user?.pubkey ?? '', limit],
    queryFn: async ({ signal }) => {
      if (!user) return [];

      // Step 1: Fetch the user's follow list (kind 3)
      const [followEvent] = await nostr.query(
        [{ kinds: [3], authors: [user.pubkey], limit: 1 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(5000)]) },
      );

      const followedPubkeys: string[] = followEvent
        ? followEvent.tags.filter(([n]) => n === 'p').map(([, pk]) => pk)
        : [];

      // Always include the user themselves
      const authors = Array.from(new Set([user.pubkey, ...followedPubkeys]));

      // Step 2: Fetch recent kind-1 notes from those authors
      const notes = await nostr.query(
        [{ kinds: [1], authors, limit, since: Math.floor(Date.now() / 1000) - 7 * 24 * 60 * 60 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(8000)]) },
      );

      return notes.sort((a, b) => b.created_at - a.created_at);
    },
    enabled: !!user,
    staleTime: 60 * 1000,
  });
}

/**
 * Fetches the replies to a specific note (kind-1 events referencing it via `e` tag).
 * Returns a flat list sorted oldest-first (chronological for a thread).
 */
export function useNoteReplies(eventId: string) {
  const { nostr } = useNostr();

  return useQuery<NostrEvent[]>({
    queryKey: ['note-replies', eventId],
    queryFn: async ({ signal }) => {
      const events = await nostr.query(
        [{ kinds: [1], '#e': [eventId], limit: 100 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(6000)]) },
      );
      return events.sort((a, b) => a.created_at - b.created_at);
    },
    enabled: !!eventId,
    staleTime: 30 * 1000,
  });
}

/**
 * Fetches a single note by id.
 */
export function useNote(eventId: string) {
  const { nostr } = useNostr();

  return useQuery<NostrEvent | null>({
    queryKey: ['note', eventId],
    queryFn: async ({ signal }) => {
      const [event] = await nostr.query(
        [{ ids: [eventId], limit: 1 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(5000)]) },
      );
      return event ?? null;
    },
    enabled: !!eventId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Helper to invalidate note-related caches after publish.
 */
export function useInvalidateNotes() {
  const qc = useQueryClient();
  return (pubkey: string, replyToId?: string) => {
    qc.invalidateQueries({ queryKey: ['note-feed', pubkey] });
    if (replyToId) {
      qc.invalidateQueries({ queryKey: ['note-replies', replyToId] });
    }
  };
}
